/**
 * @file index.tsx
 *
 * @fileoverview Renders a chip component.
 */
import { cva, VariantProps } from 'class-variance-authority';
import React from 'react';
import styles from './chip.module.scss';

const chip = cva([styles.chip], {
  variants: {
    chipStyle: {
      primary: styles.chipPrimary,
      secondary: styles.chipSecondary,
      default: styles.chipDefault,
    },
  },
  defaultVariants: {
    chipStyle: 'default',
  },
});

const icon = cva([styles.icon], {
  variants: {
    iconStyle: {
      primary: styles.iconPrimary,
      secondary: styles.iconSecondary,
      default: styles.iconDefault,
    },
  },
  defaultVariants: {
    iconStyle: 'default',
  },
});

export interface ChipProps extends VariantProps<typeof chip> {
  /**
   * The chipId is an id passed to the chip so it can be returned properly to the parent when removed.
   */
  chipId: string;
  /**
   * The content to be displayed inside the chip element.
   *
   * @default "null"
   */
  children: React.ReactNode | string;
  /**
   * The theme props is passed automatically to the component via styled components.
   *
   * @default null
   */
  theme?: any;
  /**
   * The style for the chip. The color of the chip element will be changed accordingly.
   *
   * @default "default"
   */
  chipStyle?: 'primary' | 'secondary' | 'default';
  /**
   * The event that gets fired when a chip is removed.
   * Will pass the chipId back to the parent so it knows whats being removed.
   *
   * @default "null"
   */
  onRemove?(chipId: string): void;
}

/**
 * The chip is a label style element that can be used to show information in a supporting manner. It has the option to be toggled off in case
 * its information one wants to be available to remove. It can be used to show facets that are selected or other information
 * that supports some sort of "state" for the UI.
 */
export function Chip({
  chipStyle = 'default',
  chipId,
  children,
  onRemove,
}: ChipProps) {
  return (
    <div data-testid={`chip-${chipId}`} className={chip({ chipStyle })}>
      {children}
      {onRemove && (
        <button
          data-testid={`chip-${chipId}-remove`}
          id={`chip-${chipId}-remove`}
          className={icon({ iconStyle: chipStyle })}
          aria-label={
            typeof children === 'string'
              ? `Close ${children}`
              : `Close ${chipId}`
          }
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            onRemove(chipId)
          }
        />
      )}
    </div>
  );
}
export default Chip;
